import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Carrier, CarrierEnabledScope, Site } from '@klareospace/pharos-lib-types';

import { useUpdateSiteEnablement } from './use-update-site-enablement';

// ─── Types ────────────────────────────────────────────────────────────────────

export type UseEditSiteEnablementFormReturn = {
  from: Date | null | undefined;
  isPending: boolean;
  onSubmit: () => void;
  setFrom: (date: Date | null | undefined) => void;
  setTo: (date: Date | null | undefined) => void;
  to: Date | null | undefined;
  validationError: string | null;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useEditSiteEnablementForm(
  carrier: Carrier | undefined,
  scope: CarrierEnabledScope | undefined,
  site: Site | undefined,
  onClose: () => void,
): UseEditSiteEnablementFormReturn {
  const { t } = useTranslation('settings');
  const updateSiteEnablement = useUpdateSiteEnablement();
  const [from, setFrom] = useState<Date | null | undefined>(scope?.from ? new Date(scope.from) : undefined);
  const [to, setTo] = useState<Date | null | undefined>(scope?.to ? new Date(scope.to) : undefined);

  // Reset state when scope changes
  useEffect(() => {
    setFrom(scope?.from ? new Date(scope.from) : undefined);
    setTo(scope?.to ? new Date(scope.to) : undefined);
  }, [scope]);

  const validationError = useMemo<string | null>(() => {
    if (from && to && new Date(from) >= new Date(to)) {
      return t('sites.settings.carriers.validation.startBeforeEnd');
    }
    return null;
  }, [from, t, to]);

  function onSubmit(): void {
    if (!carrier || !site || !scope || validationError) return;
    updateSiteEnablement
      .mutateAsync({
        carrierId: carrier.id,
        from,
        siteId: site.id,
        spaceId: scope.spaceId,
        to,
      })
      .then(
        () => onClose(),
        () => {
          // error handled in hook via toast
        },
      );
  }

  return { from, isPending: updateSiteEnablement.isPending, onSubmit, setFrom, setTo, to, validationError };
}
